import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useForm,
} from "../../hooks";

import { InitialStateMinimumRequirements_OneTimePassword_Form } from "./one-time-password-intitial-state";

import {
  OneTimePasswordFormApp_VerifyCode,
  OneTimePasswordFormInitialState_VerifyCode,
  OneTimePasswordFormLogic_VerifyCode,
  OneTimePasswordForm_VerifyCode,
} from "./one-time-password-verify-code-form";

interface OneTimePasswordVerifyCodeFormConsumerInterface
  extends GenericFormConsumerInterface<OneTimePasswordForm_VerifyCode> {}
const defaultValue =
  {} as unknown as OneTimePasswordVerifyCodeFormConsumerInterface;
const OneTimePasswordVerifyCodeFormContext = React.createContext(defaultValue);

interface OneTimePasswordVerifyCodeForm_ControllerConsumerInterface {
  isVerifyCodeFormCompleteAndValid: () => boolean;
  resetForm: () => void;
}
const defaultControllerValue =
  {} as unknown as OneTimePasswordVerifyCodeForm_ControllerConsumerInterface;
const OneTimePasswordVerifyCodeForm_ControllerContext = React.createContext(
  defaultControllerValue
);

export interface OneTimePasswordVerifyCodeFormProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_OneTimePassword_Form;
  };
}

export const OneTimePasswordVerifyCodeFormProvider: React.FunctionComponent<
  OneTimePasswordVerifyCodeFormProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const form = useForm<
    OneTimePasswordForm_VerifyCode,
    OneTimePasswordFormApp_VerifyCode
  >(
    OneTimePasswordFormLogic_VerifyCode,
    () => ({
      initialState,
    }),
    OneTimePasswordFormInitialState_VerifyCode
  );

  const isVerifyCodeFormCompleteAndValid: OneTimePasswordVerifyCodeForm_ControllerConsumerInterface["isVerifyCodeFormCompleteAndValid"] =
    React.useCallback(() => {
      return form.validateRightNowAndGiveMeAYesOrNo();
    }, [form]);

  const value: OneTimePasswordVerifyCodeFormConsumerInterface = React.useMemo(
    () => ({
      ...GenericReactProviderFormInterfaceOutput(form),
    }),
    [form]
  );
  const controllerValue: OneTimePasswordVerifyCodeForm_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        isVerifyCodeFormCompleteAndValid,
        resetForm: form.resetForm,
      }),
      [form, isVerifyCodeFormCompleteAndValid]
    );
  return (
    <OneTimePasswordVerifyCodeFormContext.Provider value={value}>
      <OneTimePasswordVerifyCodeForm_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </OneTimePasswordVerifyCodeForm_ControllerContext.Provider>
    </OneTimePasswordVerifyCodeFormContext.Provider>
  );
};

export const useOneTimePasswordVerifyCodeForm = () => {
  const context = React.useContext(OneTimePasswordVerifyCodeFormContext);
  if (context === defaultValue) {
    throw new Error(
      "useOneTimePasswordVerifyCodeForm must be used within a OneTimePasswordVerifyCodeFormProvider"
    );
  }
  return context;
};

export const useOneTimePasswordVerifyCodeForm_asController = () => {
  const context = React.useContext(
    OneTimePasswordVerifyCodeForm_ControllerContext
  );
  if (context === defaultControllerValue) {
    throw new Error(
      "useOneTimePasswordVerifyCodeForm_asController must be used within a OneTimePasswordVerifyCodeFormProvider"
    );
  }
  return context;
};
